/**
 * Mappings → Techs' summary row (the owner, 2026-10-19): one chip per area,
 * worded as Mappings → Clients words its own where they fit (summaryRows.ts).
 * Each says what the tech's open panel (TechPanel.tsx) shows in full: green
 * when it's done, amber when it needs the owner, grey when there's nothing.
 */
import type { Tech, TechMapping } from "../../api/types";
import { NOT_MAPPED, type Status } from "./summaryRows";

export function userName(tech: Tech): string {
  return tech.display_name ?? tech.email ?? "Unnamed user";
}

const MAPPED: Status = { label: "Mapped", tone: "done" };
const SUGGESTED: Status = { label: "Suggested", tone: "todo" };

/**
 * The tech's Autotask resource. An active user without one is "Missing", the
 * users the page's count line counts ("N active without an Autotask
 * resource"), or "Suggested" when one has their email. A resource that can't
 * be used, or with no default role, needs attention as the panel says.
 */
export function autotaskStatus(tech: Tech, mapping: TechMapping): Status {
  if (tech.autotask_resource_id === null) {
    if (!tech.is_active) {
      return NOT_MAPPED;
    }
    return tech.suggested_resource_id !== null &&
      mapping.resources.some((r) => r.autotask_resource_id === tech.suggested_resource_id)
      ? SUGGESTED
      : { label: "Missing", tone: "todo" };
  }
  const resource = mapping.resources.find((r) => r.autotask_resource_id === tech.autotask_resource_id);
  if (resource === undefined || !resource.available) {
    return { label: "Needs attention", tone: "todo" };
  }
  return resource.default_role === null ? { label: "No default role", tone: "todo" } : MAPPED;
}

/** The tech's one SkySwitch extension (ADR 0078): several from before are to pick from. */
export function skySwitchStatus(tech: Tech, mapping: TechMapping): Status {
  const mine = tech.skyswitch_extensions;
  if (mine.length > 1) {
    return { label: "Pick one", tone: "todo" };
  }
  const [key] = mine;
  if (key !== undefined) {
    return mapping.extensions.some((e) => e.key === key && e.available)
      ? MAPPED
      : { label: "Not in SkySwitch", tone: "todo" };
  }
  return tech.suggested_extensions.length > 0 ? SUGGESTED : NOT_MAPPED;
}

/** The tech's ScreenConnect name, and whether they have a work computer (ADR 0162). */
export function screenConnectStatus(tech: Tech): Status {
  const computers = tech.work_computers.length;
  const computer =
    computers === 0 ? null : computers === 1 ? "work computer" : `${String(computers)} work computers`;
  if (tech.screenconnect_username !== null) {
    return { label: computer === null ? "Mapped" : `Mapped · ${computer}`, tone: "done" };
  }
  return computer === null ? NOT_MAPPED : { label: `No name · ${computer}`, tone: "none" };
}

/** The tech's one Datto RMM user (ADR 0080); the one with their email is suggested. */
export function dattoStatus(tech: Tech): Status {
  if (tech.datto_rmm_username !== null) {
    return MAPPED;
  }
  return tech.suggested_datto_username !== null ? SUGGESTED : NOT_MAPPED;
}

/** Whether the tech's mail is read (ADR 0108 decision 3), or Exchange refused it. */
export function mailStatus(tech: Tech): Status {
  if (tech.read_mail && tech.mail_refused_at !== null) {
    return { label: "Refused", tone: "todo" };
  }
  if (tech.read_mail && tech.is_active) {
    return { label: "Reading mail", tone: "done" };
  }
  return tech.email === null && !tech.read_mail
    ? { label: "No mailbox", tone: "none" }
    : { label: "Not read", tone: "none" };
}

/** How many office addresses the tech has (ADR 0158), their work computer's included. */
export function officeStatus(tech: Tech): Status {
  const count = tech.office_addresses.length;
  return count > 0
    ? { label: `${String(count)} ${count === 1 ? "address" : "addresses"}`, tone: "done" }
    : NOT_MAPPED;
}
